import React, { useEffect, useState, useRef } from "react";
import {
  db,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from "../firebase";
import { v4 as uuidv4 } from "uuid";
import LoadingSpinner from "../components/LoadingSpinner";
import CollapsibleSection from "../components/CollapsibleSection";
import { useNavigate } from "react-router-dom";

const FeedbackModal = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>{type === "success" ? "Success" : "Error"}</h3>
        <p style={{ color: type === "success" ? "#15803d" : "#b91c1c" }}>
          {message}
        </p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionModal = ({ truck, onClose, onEdit, onAssign, onDispose, onServiceNotes, onViewMore, onComplianceDocs, hasActiveTrips }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Actions for Truck: {truck.reg_no}</h3>
        {hasActiveTrips ? (
          <p style={{ color: "#b91c1c" }}>
            This truck is currently on an active trip. You can only view service notes or details.
          </p>
        ) : (
          <p>Select an action to perform on this truck.</p>
        )}
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={onEdit}
            disabled={hasActiveTrips}
            style={{ opacity: hasActiveTrips ? 0.5 : 1 }}
          >
            Edit
          </button>
          <button
            className="btn-secondary"
            onClick={onAssign}
            disabled={hasActiveTrips}
            style={{ opacity: hasActiveTrips ? 0.5 : 1 }}
          >
            Assign
          </button>
          <button
            className="btn-delete"
            onClick={onDispose}
            disabled={hasActiveTrips}
            style={{ opacity: hasActiveTrips ? 0.5 : 1 }}
          >
            Dispose
          </button>
          <button className="btn-secondary" onClick={onServiceNotes}>
            Service Notes
          </button>
          <button className="btn-secondary" onClick={onViewMore}>
            View More
          </button>
          <button className="btn-secondary" onClick={onComplianceDocs}>
            Compliance Documents
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const DisposeModal = ({ truck, onClose, onDispose, trips }) => {
  const [disposeForm, setDisposeForm] = useState({
    type: "write-off",
    cost: "",
    amount: "",
    reason: "accident",
    condition: "functional",
  });
  const [feedback, setFeedback] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setFeedback(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDispose = async (e) => {
    e.preventDefault();
    setFeedback(null);
    try {
      if (hasActiveTrips(truck.id, trips)) {
        setFeedback({
          message: "Cannot dispose this truck because it is on an active trip.",
          type: "error",
        });
        return;
      }
      if (disposeForm.type === "write-off" && !disposeForm.cost) {
        setFeedback({ message: "Please enter the cost for write-off.", type: "error" });
        return;
      }
      if (disposeForm.type === "sold" && !disposeForm.amount) {
        setFeedback({ message: "Please enter the sale amount.", type: "error" });
        return;
      }
      const payload = {
        truck_id: truck.id,
        reg_no: truck.reg_no,
        type: disposeForm.type,
        [disposeForm.type === "write-off" ? "cost" : "amount"]: parseFloat(
          disposeForm.type === "write-off" ? disposeForm.cost : disposeForm.amount
        ),
        [disposeForm.type === "write-off" ? "reason" : "condition"]:
          disposeForm[disposeForm.type === "write-off" ? "reason" : "condition"],
        date: serverTimestamp(),
      };
      await addDoc(collection(db, "disposals"), payload);
      await updateDoc(doc(db, "trucks", truck.id), {
        disposed: true,
        assigned_driver: "",
        trailers: [],
      });
      setFeedback({ message: "Truck disposed successfully.", type: "success" });
      onDispose();
      onClose();
    } catch (error) {
      console.error("Error disposing truck:", error.code, error.message, error);
      setFeedback({
        message:
          error.code === "unavailable" || error.code === "deadline-exceeded"
            ? "Network error: Please check your connection."
            : error.code === "permission-denied"
            ? "Database error: You don't have permission to perform this action."
            : `Failed to dispose truck: ${error.message}`,
        type: "error",
      });
    }
  };

  const hasActiveTrips = (truckId, tripsList) => {
    return tripsList.some(
      (t) => t.truck === truck.reg_no && t.status === "Active"
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Dispose Truck: {truck.reg_no}</h3>
        <form onSubmit={handleDispose} className="form-block">
          <div className="form-cell">
            <label>Disposal Type</label>
            <select
              value={disposeForm.type}
              onChange={(e) =>
                setDisposeForm({ ...disposeForm, type: e.target.value })
              }
            >
              <option value="write-off">Write-Off</option>
              <option value="sold">Sold</option>
            </select>
          </div>
          {disposeForm.type === "write-off" ? (
            <>
              <div className="form-cell">
                <label>Cost</label>
                <input
                  type="number"
                  placeholder="Cost of write-off"
                  value={disposeForm.cost}
                  onChange={(e) =>
                    setDisposeForm({ ...disposeForm, cost: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-cell">
                <label>Reason</label>
                <select
                  value={disposeForm.reason}
                  onChange={(e) =>
                    setDisposeForm({ ...disposeForm, reason: e.target.value })
                  }
                >
                  <option value="accident">Accident</option>
                  <option value="mechanical_failure">Mechanical Failure</option>
                  <option value="obsolescence">Obsolescence</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="form-cell">
                <label>Amount</label>
                <input
                  type="number"
                  placeholder="Sale amount"
                  value={disposeForm.amount}
                  onChange={(e) =>
                    setDisposeForm({ ...disposeForm, amount: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-cell">
                <label>Condition</label>
                <select
                  value={disposeForm.condition}
                  onChange={(e) =>
                    setDisposeForm({ ...disposeForm, condition: e.target.value })
                  }
                >
                  <option value="functional">Functional</option>
                  <option value="scrap">Scrap</option>
                </select>
              </div>
            </>
          )}
          <div className="modal-actions">
            <button type="submit" className="btn-primary">
              Dispose
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
        {feedback && (
          <FeedbackModal
            message={feedback.message}
            type={feedback.type}
            onClose={() => setFeedback(null)}
          />
        )}
      </div>
    </div>
  );
};

const AssignModal = ({ truck, onClose, onAssign, drivers, trailers, trips }) => {
  const [assignForm, setAssignForm] = useState({
    assigned_driver: truck.assigned_driver || "",
    trailers: truck.trailers || [],
  });
  const [feedback, setFeedback] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setFeedback(null);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasActiveTrips = (truckId, tripsList) => {
    return tripsList.some(
      (t) => t.truck === truck.reg_no && t.status === "Active"
    );
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setFeedback(null);
    try {
      if (hasActiveTrips(truck.id, trips)) {
        setFeedback({
          message: "Cannot assign this truck because it is on an active trip.",
          type: "error",
        });
        return;
      }
      await updateDoc(doc(db, "trucks", truck.id), {
        assigned_driver: assignForm.assigned_driver,
        trailers: assignForm.trailers,
      });
      const trailerUpdates = trailers.map(async (trailer) => {
        if (assignForm.trailers.includes(trailer.registration)) {
          await updateDoc(doc(db, "trailers", trailer.id), {
            assigned_truck: truck.reg_no,
          });
        } else if (trailer.assigned_truck === truck.reg_no) {
          await updateDoc(doc(db, "trailers", trailer.id), {
            assigned_truck: "",
          });
        }
      });
      await Promise.all(trailerUpdates);
      setFeedback({ message: "Truck assigned successfully.", type: "success" });
      onAssign();
      onClose();
    } catch (error) {
      console.error("Error assigning truck:", error.code, error.message, error);
      setFeedback({
        message:
          error.code === "unavailable" || error.code === "deadline-exceeded"
            ? "Network error: Please check your connection."
            : error.code === "permission-denied"
            ? "Database error: You don't have permission to perform this action."
            : `Failed to assign truck: ${error.message}`,
        type: "error",
      });
    }
  };

  const renderDriverDropdown = () => {
    const isPlaceholder = !assignForm.assigned_driver;
    return (
      <div className="dropdown-wrapper" ref={dropdownRef}>
        <div
          className={`select-control dropdown-input ${
            openDropdown === "assigned_driver" ? "active" : ""
          } ${isPlaceholder ? "placeholder" : ""}`}
          onClick={() =>
            setOpenDropdown(
              openDropdown === "assigned_driver" ? null : "assigned_driver"
            )
          }
        >
          {isPlaceholder ? "Select Driver" : assignForm.assigned_driver}
        </div>
        {openDropdown === "assigned_driver" && (
          <div className="dropdown">
            <div
              className={`dropdown-option ${
                !assignForm.assigned_driver ? "selected" : ""
              }`}
              onClick={() => {
                setAssignForm({ ...assignForm, assigned_driver: "" });
                setOpenDropdown(null);
              }}
            >
              None
            </div>
            {drivers.length > 0 ? (
              drivers.map((driver) => (
                <div
                  key={driver.id}
                  className={`dropdown-option ${
                    assignForm.assigned_driver === driver.full_name
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => {
                    setAssignForm({
                      ...assignForm,
                      assigned_driver: driver.full_name,
                    });
                    setOpenDropdown(null);
                  }}
                >
                  {driver.full_name}
                </div>
              ))
            ) : (
              <div className="dropdown-option">No drivers available</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTrailersDropdown = () => {
    return (
      <div className="form-cell">
        <label>Trailers</label>
        <select
          multiple
          value={assignForm.trailers}
          onChange={(e) =>
            setAssignForm({
              ...assignForm,
              trailers: Array.from(
                e.target.selectedOptions,
                (option) => option.value
              ),
            })
          }
          style={{ height: "100px" }}
        >
          {trailers.map((trailer) => (
            <option key={trailer.id} value={trailer.registration}>
              {trailer.registration} ({trailer.type || "Unknown"})
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Assign Truck: {truck.reg_no}</h3>
        <form onSubmit={handleAssign} className="form-block">
          {renderDriverDropdown()}
          {renderTrailersDropdown()}
          <div className="modal-actions">
            <button type="submit" className="btn-primary">
              Save
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
        {feedback && (
          <FeedbackModal
            message={feedback.message}
            type={feedback.type}
            onClose={() => setFeedback(null)}
          />
        )}
      </div>
    </div>
  );
};

const ViewMoreModal = ({ truck, onClose, calculateRemainingKm, calculatePredictedServiceDate }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Truck Details: {truck.reg_no}</h3>
        <div className="form-block">
          <div className="form-cell">
            <label>Registration Number</label>
            <p>{truck.reg_no || "-"}</p>
          </div>
          <div className="form-cell">
            <label>Make</label>
            <p>{truck.make || "-"}</p>
          </div>
          <div className="form-cell">
            <label>Model</label>
            <p>{truck.model || "-"}</p>
          </div>
          <div className="form-cell">
            <label>Current Odometer (km)</label>
            <p>{truck.current_odometer || "-"}</p>
          </div>
          <div className="form-cell">
            <label>Status</label>
            <p>{truck.status || "Active"}</p>
          </div>
          <div className="form-cell">
            <label>Truck Code</label>
            <p>{truck.truck_code || "-"}</p>
          </div>
          <div className="form-cell">
            <label>Service Interval (km)</label>
            <p>{truck.service_interval_km || "-"}</p>
          </div>
          <div className="form-cell">
            <label>Last Service Date</label>
            <p>{truck.last_service_date || "-"}</p>
          </div>
          <div className="form-cell">
            <label>Odometer at Service (km)</label>
            <p>{truck.odometer_at_service || "-"}</p>
          </div>
          <div className="form-cell">
            <label>Book Value</label>
            <p>{truck.book_value ? `$${truck.book_value}` : "-"}</p>
          </div>
          <div className="form-cell">
            <label>Assigned Driver</label>
            <p>{truck.assigned_driver || "-"}</p>
          </div>
          <div className="form-cell">
            <label>Trailers</label>
            <p>{truck.trailers?.length > 0 ? truck.trailers.join(", ") : "-"}</p>
          </div>
          <div className="form-cell">
            <label>Remaining to Service (km)</label>
            <p
              className={
                Number(calculateRemainingKm(truck)) <= 500 ? "service-warning" : ""
              }
            >
              {calculateRemainingKm(truck)}
            </p>
          </div>
          <div className="form-cell">
            <label>Predicted Service Date</label>
            <p>{calculatePredictedServiceDate(truck)}</p>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const TruckDocumentsModal = ({ truck, documents, onClose }) => {
  const navigate = useNavigate();
  const modalRef = useRef(null);

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const exp = new Date(expiryDate);
    const today = new Date();
    const diffTime = exp - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 14 && diffDays >= 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const truckDocs = documents.filter(
    (d) => d.owner_type === "truck" && d.owner_id === truck.reg_no
  );

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef} style={{ maxWidth: "900px" }}>
        <h3>Compliance Documents - {truck.reg_no}</h3>
        {truckDocs.length === 0 ? (
          <p>No compliance documents found for this truck.</p>
        ) : (
          <div className="table-wrap">
            <table className="simple-table">
              <thead>
                <tr>
                  <th>Document Type</th>
                  <th>Issue Date</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                  <th>Shared</th>
                  <th>File</th>
                </tr>
              </thead>
              <tbody>
                {truckDocs.map((doc) => (
                  <tr key={doc.id}>
                    <td>{doc.doc_type || "-"}</td>
                    <td>{doc.issue_date || "-"}</td>
                    <td>{doc.expiry_date || "-"}</td>
                    <td>
                      {isExpired(doc.expiry_date) ? (
                        <span style={{ color: "#b91c1c", fontWeight: "bold" }}>Expired</span>
                      ) : isExpiringSoon(doc.expiry_date) ? (
                        <span style={{ color: "#d97706", fontWeight: "bold" }}>Expiring Soon</span>
                      ) : (
                        <span style={{ color: "#15803d" }}>Valid</span>
                      )}
                    </td>
                    <td>{doc.is_shared ? "Yes" : "No"}</td>
                    <td>
                      {doc.file_path ? (
                        <a href={doc.file_path} target="_blank" rel="noopener noreferrer">
                          View File
                        </a>
                      ) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="modal-actions">
          <button
            className="btn-primary"
            onClick={() => navigate("/documents", { state: { filterTruck: truck.reg_no } })}
          >
            Go to Documents Page
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const PostSavePromptModal = ({ onYes, onNo }) => {
  const modalRef = useRef(null);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef} style={{ maxWidth: "500px" }}>
        <h3>Add Compliance Documents?</h3>
        <p>Would you like to add or review compliance documents (Disc, Cross Border Permit, etc.) for this truck?</p>
        <div className="modal-actions">
          <button className="btn-primary" onClick={onYes}>
            Yes
          </button>
          <button className="btn-secondary" onClick={onNo}>
            No, Just Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Trucks() {
  const navigate = useNavigate();
  const [trucks, setTrucks] = useState([]);
  const [documents, setDocuments] = useState([]); // New
  const [fuelEntries, setFuelEntries] = useState([]);
  const [trips, setTrips] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [serviceRecords, setServiceRecords] = useState({});
  const [form, setForm] = useState({
    reg_no: "",
    make: "",
    model: "",
    current_odometer: "",
    status: "Active",
    truck_code: "",
    service_interval_km: "",
    last_service_date: "",
    odometer_at_service: "",
    service_notes: "",
    book_value: "",
    assigned_driver: "",
  });
  const [editingTruckId, setEditingTruckId] = useState(null);
  const [serviceModalOpen, setServiceModalOpen] = useState(null);
  const [disposeTruckId, setDisposeTruckId] = useState(null);
  const [assignTruckId, setAssignTruckId] = useState(null);
  const [actionTruckId, setActionTruckId] = useState(null);
  const [viewMoreTruckId, setViewMoreTruckId] = useState(null);
  const [documentsModalTruck, setDocumentsModalTruck] = useState(null);
  const [showPostSavePrompt, setShowPostSavePrompt] = useState(false);
  const [lastSavedTruck, setLastSavedTruck] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    summaryCards: true,
    truckForm: true,
    trucksList: true,
  });
  const modalRef = useRef(null);
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      setFeedback(null);
      try {
        await Promise.all([
          loadTrucks(),
          loadDocuments(),
          loadFuelEntries(),
          loadTrips(),
          loadRoutes(),
          loadDrivers(),
          loadTrailers(),
        ]);
        console.log("Trucks, documents, fuel, trips, routes, drivers, and trailers loaded");
      } catch (error) {
        console.error("Error loading data:", error.code, error.message, error);
        setFeedback({
          message:
            error.code === "unavailable" || error.code === "deadline-exceeded"
              ? "Network error: Please check your connection. Ensure Firebase emulator is running if on localhost."
              : error.code === "permission-denied"
              ? "Database error: You don't have permission to access this data."
              : `Failed to load data: ${error.message}`,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeModal();
        setServiceModalOpen(null);
        setDisposeTruckId(null);
        setAssignTruckId(null);
        setActionTruckId(null);
        setViewMoreTruckId(null);
        setDocumentsModalTruck(null);
        setShowPostSavePrompt(false);
        setSelectedService(null);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function loadDocuments() {
    try {
      const snaps = await getDocs(collection(db, "documents"));
      const docsData = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDocuments(docsData);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    }
  }

  async function loadTrucks() {
    try {
      const snaps = await getDocs(collection(db, "trucks"));
      const trucksData = snaps.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
          disposed: d.data().disposed ?? false,
        }))
        .filter((truck) => !truck.disposed);
      console.log("Fetched trucks:", trucksData);
      setTrucks(trucksData);

      const serviceData = {};
      for (const truck of trucksData) {
        const serviceSnaps = await getDocs(
          collection(db, `trucks/${truck.id}/services`)
        );
        serviceData[truck.id] = serviceSnaps.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
      }
      console.log("Fetched service records:", serviceData);
      setServiceRecords(serviceData);
    } catch (error) {
      console.error("Error fetching trucks:", error.code, error.message, error);
      throw error;
    }
  }

  async function loadFuelEntries() {
    try {
      const snaps = await getDocs(collection(db, "trip_finances"));
      const fuelData = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log("Fetched fuel entries:", fuelData);
      setFuelEntries(fuelData);
    } catch (error) {
      console.error("Error fetching fuel entries:", error.code, error.message, error);
      throw error;
    }
  }

  async function loadTrips() {
    try {
      const snaps = await getDocs(collection(db, "trips"));
      const tripsData = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log("Fetched trips:", tripsData);
      setTrips(tripsData);
    } catch (error) {
      console.error("Error fetching trips:", error.code, error.message, error);
      throw error;
    }
  }

  async function loadRoutes() {
    try {
      const snaps = await getDocs(collection(db, "routes"));
      const routesData = snaps.docs.map((r) => ({ id: r.id, ...r.data() }));
      console.log("Fetched routes:", routesData);
      setRoutes(routesData);
    } catch (error) {
      console.error("Error fetching routes:", error.code, error.message, error);
      throw error;
    }
  }

  async function loadDrivers() {
    try {
      const snaps = await getDocs(collection(db, "drivers"));
      const driversData = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log("Fetched drivers:", driversData);
      setDrivers(driversData);
    } catch (error) {
      console.error("Error fetching drivers:", error.code, error.message, error);
      throw error;
    }
  }

  async function loadTrailers() {
    try {
      const snaps = await getDocs(collection(db, "trailers"));
      const trailersData = snaps.docs.map((t) => ({
        id: t.id,
        registration: t.data().registration || "Unknown",
        type: t.data().type || "Unknown",
        assigned_truck: t.data().assigned_truck || "",
      }));
      console.log("Fetched trailers:", trailersData);
      setTrailers(trailersData);
    } catch (error) {
      console.error("Error fetching trailers:", error.code, error.message, error);
      throw error;
    }
  }

  async function validateRegNo(reg_no, excludeId = null) {
    try {
      const q = query(collection(db, "trucks"), where("reg_no", "==", reg_no));
      const snaps = await getDocs(q);
      return snaps.docs.every((d) => d.id === excludeId);
    } catch (error) {
      console.error("Error validating reg_no:", error.code, error.message, error);
      throw error;
    }
  }

  const getExpiringComplianceCount = () => {
    const today = new Date();
    const in14Days = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);

    return documents.filter((doc) => {
      if (doc.owner_type !== "truck" || !doc.expiry_date) return false;
      const exp = new Date(doc.expiry_date);
      return exp >= today && exp <= in14Days;
    }).length;
  };

  const metrics = {
    totalTrucks: trucks.length,
    dueForService: trucks.filter((t) => Number(calculateRemainingKm(t)) <= 500).length,
    activeTrucks: trucks.filter((t) => t.status === "Active").length,
    inactiveTrucks: trucks.filter((t) => t.status === "Inactive").length,
    maintenanceTrucks: trucks.filter((t) => t.status === "Maintenance").length,
    expiringCompliance: getExpiringComplianceCount(),
  };

  const handleSectionToggle = (sectionId) => {
    setExpandedSections({
      ...expandedSections,
      [sectionId]: !expandedSections[sectionId],
    });
  };

  async function createTruck(e) {
    e.preventDefault();
    setFeedback(null);
    try {
      const { reg_no, make, model, current_odometer, service_interval_km, book_value } = form;
      if (!reg_no || !make || !model) {
        setFeedback({
          message: "Please provide registration number, make, and model.",
          type: "error",
        });
        return;
      }
      if (Number(current_odometer) < 0) {
        setFeedback({ message: "Odometer reading cannot be negative.", type: "error" });
        return;
      }
      if (Number(service_interval_km) <= 0) {
        setFeedback({ message: "Service interval must be a positive number.", type: "error" });
        return;
      }
      if (book_value && isNaN(parseFloat(book_value))) {
        setFeedback({ message: "Book value must be a valid number.", type: "error" });
        return;
      }
      const isUnique = await validateRegNo(reg_no);
      if (!isUnique) {
        setFeedback({ message: "Registration number must be unique.", type: "error" });
        return;
      }
      const payload = {
        reg_no,
        make,
        model,
        current_odometer: Number(current_odometer) || 0,
        status: form.status,
        truck_code: "TRK-" + uuidv4().slice(0, 8),
        service_interval_km: Number(service_interval_km) || 0,
        book_value: book_value ? parseFloat(book_value) : 0,
        assigned_driver: form.assigned_driver || "",
        trailers: [],
        disposed: false,
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "trucks"), payload);
      setLastSavedTruck({ id: docRef.id, reg_no: reg_no });
      setShowPostSavePrompt(true);
      setForm({
        reg_no: "",
        make: "",
        model: "",
        current_odometer: "",
        status: "Active",
        truck_code: "",
        service_interval_km: "",
        last_service_date: "",
        odometer_at_service: "",
        service_notes: "",
        book_value: "",
        assigned_driver: "",
      });
      setFeedback({ message: "Truck added successfully.", type: "success" });
      await loadTrucks();
    } catch (error) {
      console.error("Error creating truck:", error.code, error.message, error);
      setFeedback({
        message:
          error.code === "unavailable" || error.code === "deadline-exceeded"
            ? "Network error: Please check your connection."
            : error.code === "permission-denied"
            ? "Database error: You don't have permission to perform this action."
            : `Failed to create truck: ${error.message}`,
        type: "error",
      });
    }
  }

  async function updateTruck(e) {
    e.preventDefault();
    if (!editingTruckId) return;
    setFeedback(null);
    try {
      const { reg_no, make, model, current_odometer, service_interval_km, book_value } = form;
      if (!reg_no || !make || !model) {
        setFeedback({
          message: "Please provide registration number, make, and model.",
          type: "error",
        });
        return;
      }
      if (Number(current_odometer) < 0) {
        setFeedback({ message: "Odometer reading cannot be negative.", type: "error" });
        return;
      }
      if (Number(service_interval_km) <= 0) {
        setFeedback({ message: "Service interval must be a positive number.", type: "error" });
        return;
      }
      if (book_value && isNaN(parseFloat(book_value))) {
        setFeedback({ message: "Book value must be a valid number.", type: "error" });
        return;
      }
      const isUnique = await validateRegNo(reg_no, editingTruckId);
      if (!isUnique) {
        setFeedback({ message: "Registration number must be unique.", type: "error" });
        return;
      }
      const payload = {
        reg_no,
        make,
        model,
        current_odometer: Number(current_odometer) || 0,
        status: form.status,
        truck_code: form.truck_code,
        service_interval_km: Number(service_interval_km) || 0,
        assigned_driver: form.assigned_driver || "",
        book_value: book_value ? parseFloat(book_value) : 0,
      };
      await updateDoc(doc(db, "trucks", editingTruckId), payload);
      setLastSavedTruck({ id: editingTruckId, reg_no: reg_no });
      setShowPostSavePrompt(true);
      setFeedback({ message: "Truck updated successfully.", type: "success" });
      closeModal();
      await loadTrucks();
    } catch (error) {
      console.error("Error updating truck:", error.code, error.message, error);
      setFeedback({
        message:
          error.code === "unavailable" || error.code === "deadline-exceeded"
            ? "Network error: Please check your connection."
            : error.code === "permission-denied"
            ? "Database error: You don't have permission to perform this action."
            : `Failed to update truck: ${error.message}`,
        type: "error",
      });
    }
  }

  async function addServiceRecord(e, truckId) {
    e.preventDefault();
    setFeedback(null);
    try {
      const { last_service_date, odometer_at_service } = form;
      if (!last_service_date) {
        setFeedback({ message: "Please provide the last service date.", type: "error" });
        return;
      }
      if (new Date(last_service_date) > new Date()) {
        setFeedback({ message: "Service date cannot be in the future.", type: "error" });
        return;
      }
      if (
        Number(odometer_at_service) < 0 ||
        Number(odometer_at_service) > Number(form.current_odometer)
      ) {
        setFeedback({
          message:
            "Odometer at service must be between 0 and the current odometer reading.",
          type: "error",
        });
        return;
      }
      const servicePayload = {
        service_date: last_service_date,
        odometer_at_service: Number(odometer_at_service) || 0,
        service_notes: form.service_notes || "",
      };
      await addDoc(collection(db, `trucks/${truckId}/services`), servicePayload);
      await updateDoc(doc(db, "trucks", truckId), {
        last_service_date,
        odometer_at_service: Number(odometer_at_service) || 0,
      });
      setForm({
        ...form,
        last_service_date: "",
        odometer_at_service: "",
        service_notes: "",
      });
      setFeedback({ message: "Service record added successfully.", type: "success" });
      await loadTrucks();
    } catch (error) {
      console.error("Error adding service record:", error.code, error.message, error);
      setFeedback({
        message:
          error.code === "unavailable" || error.code === "deadline-exceeded"
            ? "Network error: Please check your connection."
            : error.code === "permission-denied"
            ? "Database error: You don't have permission to perform this action."
            : `Failed to add service record: ${error.message}`,
        type: "error",
      });
    }
  }

  const hasActiveTrips = (truckId, tripsList) => {
    const truck = trucks.find((t) => t.id === truckId);
    return tripsList.some(
      (t) => t.truck === truck?.reg_no && t.status === "Active"
    );
  };

  function openEditModal(truck) {
    setForm({
      reg_no: truck.reg_no,
      make: truck.make || "",
      model: truck.model || "",
      current_odometer: truck.current_odometer || "",
      status: truck.status || "Active",
      truck_code: truck.truck_code || "",
      service_interval_km: truck.service_interval_km || "",
      last_service_date: "",
      odometer_at_service: "",
      service_notes: "",
      book_value: truck.book_value || "",
      assigned_driver: truck.assigned_driver || "",
    });
    setEditingTruckId(truck.id);
    setDisposeTruckId(null);
    setAssignTruckId(null);
    setActionTruckId(null);
    setViewMoreTruckId(null);
    setDocumentsModalTruck(null);
    setShowPostSavePrompt(false);
    setOpenMenu(null);
  }

  function openDisposeModal(truck) {
    setDisposeTruckId(truck.id);
    setEditingTruckId(null);
    setAssignTruckId(null);
    setServiceModalOpen(null);
    setActionTruckId(null);
    setViewMoreTruckId(null);
    setDocumentsModalTruck(null);
    setShowPostSavePrompt(false);
    setOpenMenu(null);
  }

  function openAssignModal(truck) {
    setAssignTruckId(truck.id);
    setEditingTruckId(null);
    setDisposeTruckId(null);
    setServiceModalOpen(null);
    setActionTruckId(null);
    setViewMoreTruckId(null);
    setDocumentsModalTruck(null);
    setShowPostSavePrompt(false);
    setOpenMenu(null);
  }

  function openServiceModal(truckId) {
    setServiceModalOpen(truckId);
    setEditingTruckId(null);
    setDisposeTruckId(null);
    setAssignTruckId(null);
    setActionTruckId(null);
    setViewMoreTruckId(null);
    setDocumentsModalTruck(null);
    setShowPostSavePrompt(false);
    setOpenMenu(null);
  }

  function openActionModal(truckId) {
    setActionTruckId(truckId);
    setEditingTruckId(null);
    setDisposeTruckId(null);
    setAssignTruckId(null);
    setServiceModalOpen(null);
    setViewMoreTruckId(null);
    setDocumentsModalTruck(null);
    setShowPostSavePrompt(false);
    setOpenMenu(null);
  }

  function openViewMoreModal(truck) {
    setViewMoreTruckId(truck.id);
    setEditingTruckId(null);
    setDisposeTruckId(null);
    setAssignTruckId(null);
    setServiceModalOpen(null);
    setActionTruckId(null);
    setDocumentsModalTruck(null);
    setShowPostSavePrompt(false);
    setOpenMenu(null);
  }

  function openTruckDocuments(truck) {
    setDocumentsModalTruck(truck);
    setActionTruckId(null);
  }

  function closeModal() {
    setForm({
      reg_no: "",
      make: "",
      model: "",
      current_odometer: "",
      status: "Active",
      truck_code: "",
      service_interval_km: "",
      last_service_date: "",
      odometer_at_service: "",
      service_notes: "",
      book_value: "",
      assigned_driver: "",
    });
    setEditingTruckId(null);
    setServiceModalOpen(null);
    setDisposeTruckId(null);
    setAssignTruckId(null);
    setActionTruckId(null);
    setViewMoreTruckId(null);
    setDocumentsModalTruck(null);
    setShowPostSavePrompt(false);
    setLastSavedTruck(null);
    setSelectedService(null);
    setFeedback(null);
    setOpenMenu(null);
    setOpenDropdown(null);
  }

  const renderDropdown = (label, field, options) => {
    const isPlaceholder = !form[field];
    return (
      <div className="dropdown-wrapper" ref={dropdownRef}>
        <div
          className={`select-control dropdown-input ${
            openDropdown === field ? "active" : ""
          } ${isPlaceholder ? "placeholder" : ""}`}
          onClick={() => setOpenDropdown(openDropdown === field ? null : field)}
        >
          {isPlaceholder ? `Select ${label}` : form[field]}
        </div>
        {openDropdown === field && (
          <div className="dropdown">
            <div
              className={`dropdown-option ${!form[field] ? "selected" : ""}`}
              onClick={() => {
                setForm({ ...form, [field]: "" });
                setOpenDropdown(null);
              }}
            >
              None
            </div>
            {options.length > 0 ? (
              options.map((opt) => (
                <div
                  key={opt}
                  className={`dropdown-option ${
                    form[field] === opt ? "selected" : ""
                  }`}
                  onClick={() => {
                    setForm({ ...form, [field]: opt });
                    setOpenDropdown(null);
                  }}
                >
                  {opt}
                </div>
              ))
            ) : (
              <div className="dropdown-option">No {label.toLowerCase()} available</div>
            )}
          </div>
        )}
      </div>
    );
  };

  function calculateRemainingKm(truck) {
    const truckFuelEntries = fuelEntries.filter(
      (f) => f.truck_id === truck.reg_no && f.fuel_added && f.odometer_at_fuel
    );
    const latestFuelOdometer = truckFuelEntries.reduce(
      (max, curr) => {
        return !max || curr.odometer_at_fuel > max
          ? curr.odometer_at_fuel
          : max;
      },
      0
    );
    const latestOdometer = Math.max(
      Number(truck.current_odometer) || 0,
      Number(latestFuelOdometer) || 0
    );
    const lastServiceKm =
      Number(truck.odometer_at_service) || Number(truck.current_odometer) || 0;
    const serviceInterval = Number(truck.service_interval_km) || 0;
    const kmSinceLastService = latestOdometer - lastServiceKm;
    return Math.max(0, serviceInterval - kmSinceLastService).toFixed(0);
  }

  function calculatePredictedServiceDate(truck) {
    const remainingKm = Number(calculateRemainingKm(truck));
    if (!remainingKm || isNaN(remainingKm)) return "-";
    const activeTrip = trips.find(
      (t) => t.truck === truck.reg_no && t.status === "Active"
    );
    if (activeTrip && activeTrip.route_id) {
      const route = routes.find((r) => r.id === activeTrip.route_id);
      if (route && route.total_distance_km) {
        const daysToService = remainingKm / Number(route.total_distance_km);
        const predictedDate = new Date(
          Date.now() + daysToService * 24 * 60 * 60 * 1000
        );
        const rangeStart = new Date(predictedDate);
        rangeStart.setDate(predictedDate.getDate() - 3);
        const rangeEnd = new Date(predictedDate);
        rangeEnd.setDate(predictedDate.getDate() + 3);
        return `${rangeStart.toISOString().slice(0, 10)} to ${rangeEnd.toISOString().slice(0, 10)}`;
      }
    }
    const truckTrips = trips.filter(
      (t) => t.truck === truck.reg_no && t.start_km && t.createdAt
    );
    if (!truckTrips.length) return "Unknown";
    const totalKm = truckTrips.reduce(
      (sum, t) => sum + (Number(t.start_km) || 0),
      0
    );
    const earliestTrip = truckTrips.reduce((earliest, t) => {
      return !earliest || new Date(t.createdAt) < new Date(earliest.createdAt)
        ? t
        : earliest;
    }, null);
    const daysSinceFirstTrip =
      (new Date() - new Date(earliestTrip.createdAt)) / (1000 * 60 * 60 * 24);
    const avgKmPerDay = daysSinceFirstTrip > 0 ? totalKm / daysSinceFirstTrip : 0;
    if (avgKmPerDay <= 0) return "Unknown";
    const daysToService = remainingKm / avgKmPerDay;
    const predictedDate = new Date(
      Date.now() + daysToService * 24 * 60 * 60 * 1000
    );
    const rangeStart = new Date(predictedDate);
    rangeStart.setDate(predictedDate.getDate() - 3);
    const rangeEnd = new Date(predictedDate);
    rangeEnd.setDate(predictedDate.getDate() + 3);
    return `${rangeStart.toISOString().slice(0, 10)} to ${rangeEnd.toISOString().slice(0, 10)}`;
  }

  return (
    <div className="trucks-page">
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {feedback && (
            <FeedbackModal
              message={feedback.message}
              type={feedback.type}
              onClose={() => setFeedback(null)}
            />
          )}
          <h2>Trucks</h2>
          <CollapsibleSection
            title="Truck Summary"
            id="summaryCards"
            isExpanded={expandedSections.summaryCards}
            onToggle={() => handleSectionToggle("summaryCards")}
            externalControl={true}
          >
            <div className="summary-cards">
              <div className="card">
                <div className="card-title">Total Trucks</div>
                <div className="card-value">{metrics.totalTrucks}</div>
              </div>
              <div className="card">
                <div className="card-title">Due for Service (≤500 km)</div>
                <div className="card-value">{metrics.dueForService}</div>
              </div>
              <div className="card">
                <div className="card-title">Active Trucks</div>
                <div className="card-value">{metrics.activeTrucks}</div>
              </div>
              <div className="card">
                <div className="card-title">Inactive Trucks</div>
                <div className="card-value">{metrics.inactiveTrucks}</div>
              </div>
              <div className="card">
                <div className="card-title">Maintenance Trucks</div>
                <div className="card-value">{metrics.maintenanceTrucks}</div>
              </div>
              <div className="card">
                <div className="card-title">Compliance Docs Expiring (14 days)</div>
                <div className="card-value" style={{
                  color: metrics.expiringCompliance > 0 ? "#d97706" : "#15803d",
                  fontWeight: "bold"
                }}>
                  {metrics.expiringCompliance}
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Truck Form"
            id="truckForm"
            isExpanded={expandedSections.truckForm}
            onToggle={() => handleSectionToggle("truckForm")}
            externalControl={true}
          >
            <div className="tab-container">
              <button
                className={`btn-tab ${activeTab === "general" ? "active" : ""}`}
                onClick={() => setActiveTab("general")}
              >
                General Truck Information
              </button>
              <button
                className={`btn-tab ${activeTab === "service" ? "active" : ""}`}
                onClick={() => setActiveTab("service")}
                disabled={!editingTruckId}
              >
                Service Records
              </button>
            </div>
            <div className="tab-content">
              {activeTab === "general" && (
                <form
                  className="form-inline"
                  onSubmit={editingTruckId ? updateTruck : createTruck}
                >
                  <input
                    placeholder="Reg No"
                    value={form.reg_no}
                    onChange={(e) => setForm({ ...form, reg_no: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Make"
                    value={form.make}
                    onChange={(e) => setForm({ ...form, make: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Model"
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Current Odometer (km)"
                    type="number"
                    value={form.current_odometer}
                    onChange={(e) =>
                      setForm({ ...form, current_odometer: e.target.value })
                    }
                  />
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Maintenance">Maintenance</option>
                  </select>
                  <input
                    placeholder="Service Interval (km)"
                    type="number"
                    value={form.service_interval_km}
                    onChange={(e) =>
                      setForm({ ...form, service_interval_km: e.target.value })
                    }
                  />
                  <input
                    placeholder="Book Value"
                    type="number"
                    value={form.book_value}
                    onChange={(e) =>
                      setForm({ ...form, book_value: e.target.value })
                    }
                  />
                  {renderDropdown(
                    "Driver",
                    "assigned_driver",
                    drivers.map((d) => d.full_name)
                  )}
                  <button type="submit" className="btn-primary">
                    {editingTruckId ? "Save" : "Add Truck"}
                  </button>
                </form>
              )}
              {activeTab === "service" && editingTruckId && (
                <>
                  <p>
                    This truck is serviced every {form.service_interval_km || "N/A"} km
                  </p>
                  <form
                    className="form-inline"
                    onSubmit={(e) => addServiceRecord(e, editingTruckId)}
                  >
                    <input
                      type="date"
                      placeholder="Last Service Date"
                      value={form.last_service_date}
                      onChange={(e) =>
                        setForm({ ...form, last_service_date: e.target.value })
                      }
                      required
                    />
                    <input
                      placeholder="Odometer at Service (km)"
                      type="number"
                      value={form.odometer_at_service}
                      onChange={(e) =>
                        setForm({ ...form, odometer_at_service: e.target.value })
                      }
                    />
                    <input
                      placeholder="Service Notes"
                      value={form.service_notes}
                      onChange={(e) =>
                        setForm({ ...form, service_notes: e.target.value })
                      }
                    />
                    <button type="submit" className="btn-primary">
                      Add Service Record
                    </button>
                  </form>
                  <div className="service-notes">
                    <h4>Service History</h4>
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Service Date</th>
                          <th>Odometer (km)</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(serviceRecords[editingTruckId] || []).length === 0 ? (
                          <tr>
                            <td colSpan="3">No service records found</td>
                          </tr>
                        ) : (
                          (serviceRecords[editingTruckId] || []).map((s) => (
                            <tr
                              key={s.id}
                              className={selectedService === s.id ? "selected" : ""}
                              onClick={() => setSelectedService(s.id)}
                            >
                              <td>{s.service_date || "-"}</td>
                              <td>{s.odometer_at_service || "-"}</td>
                              <td>{s.service_notes || "-"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Trucks List"
            id="trucksList"
            isExpanded={expandedSections.trucksList}
            onToggle={() => handleSectionToggle("trucksList")}
            externalControl={true}
          >
            <div className="table-wrap">
              <table className="simple-table">
                <thead>
                  <tr>
                    <th>Reg</th>
                    <th>Make (Model)</th>
                    <th>Odometer (km)</th>
                    <th>Status</th>
                    <th>Assigned Driver</th>
                    <th>Remaining to Service (km)</th>
                    <th>Predicted Service Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trucks.length === 0 ? (
                    <tr>
                      <td colSpan="8">No trucks found</td>
                    </tr>
                  ) : (
                    trucks.map((t) => (
                      <tr
                        key={t.id}
                        style={{ opacity: hasActiveTrips(t.id, trips) ? 0.5 : 1 }}
                      >
                        <td>{t.reg_no || "-"}</td>
                        <td>
                          {t.make || "-"} ({t.model || "-"})
                        </td>
                        <td>{t.current_odometer || "-"}</td>
                        <td>{t.status || "Active"}</td>
                        <td>{t.assigned_driver || "-"}</td>
                        <td
                          className={
                            Number(calculateRemainingKm(t)) <= 500
                              ? "service-warning"
                              : ""
                          }
                        >
                          {calculateRemainingKm(t)}
                        </td>
                        <td>{calculatePredictedServiceDate(t)}</td>
                        <td style={{ position: "relative" }}>
                          <button
                            className="menu-button"
                            onClick={() => openActionModal(t.id)}
                          >
                            ⋮
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>

          {editingTruckId && (
            <div className="modal-overlay">
              <div className="modal" ref={modalRef}>
                <h3>Edit Truck: {form.reg_no}</h3>
                {/* ... existing edit modal content unchanged ... */}
              </div>
            </div>
          )}

          {serviceModalOpen && (
            <div className="modal-overlay">
              <div className="modal" ref={modalRef}>
                {/* ... existing service modal unchanged ... */}
              </div>
            </div>
          )}

          {disposeTruckId && (
            <DisposeModal
              truck={trucks.find((t) => t.id === disposeTruckId)}
              onClose={closeModal}
              onDispose={loadTrucks}
              trips={trips}
            />
          )}

          {assignTruckId && (
            <AssignModal
              truck={trucks.find((t) => t.id === assignTruckId)}
              onClose={closeModal}
              onAssign={loadTrucks}
              drivers={drivers}
              trailers={trailers}
              trips={trips}
            />
          )}

          {actionTruckId && (
            <ActionModal
              truck={trucks.find((t) => t.id === actionTruckId)}
              onClose={closeModal}
              onEdit={() => openEditModal(trucks.find((t) => t.id === actionTruckId))}
              onAssign={() => openAssignModal(trucks.find((t) => t.id === actionTruckId))}
              onDispose={() => openDisposeModal(trucks.find((t) => t.id === actionTruckId))}
              onServiceNotes={() => openServiceModal(actionTruckId)}
              onViewMore={() => openViewMoreModal(trucks.find((t) => t.id === actionTruckId))}
              onComplianceDocs={() => openTruckDocuments(trucks.find((t) => t.id === actionTruckId))}
              hasActiveTrips={hasActiveTrips(actionTruckId, trips)}
            />
          )}

          {viewMoreTruckId && (
            <ViewMoreModal
              truck={trucks.find((t) => t.id === viewMoreTruckId)}
              onClose={closeModal}
              calculateRemainingKm={calculateRemainingKm}
              calculatePredictedServiceDate={calculatePredictedServiceDate}
            />
          )}

          {documentsModalTruck && (
            <TruckDocumentsModal
              truck={documentsModalTruck}
              documents={documents}
              onClose={() => setDocumentsModalTruck(null)}
            />
          )}

          {showPostSavePrompt && lastSavedTruck && (
            <PostSavePromptModal
              onYes={() => {
                setShowPostSavePrompt(false);
                openTruckDocuments(lastSavedTruck);
              }}
              onNo={() => {
                setShowPostSavePrompt(false);
                setLastSavedTruck(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}